import { Battery, Cog, Cpu, Package, Trash2, type LucideIcon } from 'lucide-react';
import { getItem } from '@/domain/items';
import type { ItemKind, Rarity } from '@/domain/types';
import { ITEM_SPRITES, getSpriteStyle } from '@/game/itemSprites';
import { ZeusBoltSprite } from './ZeusBoltSprite';
import './ItemIcon.css';

interface ItemIconProps {
  itemId: string;
  sizeRem?: number;
  className?: string;
}

/**
 * Resolve a Blender-rendered icon variant for an equipment item.
 * Returns the filename stem (without `_<rarity>.png`) so the caller can
 * append the rarity. We have 9 variants total:
 *   weapon_sword, weapon_axe, weapon_hammer, weapon_bow,
 *   helmet, armor, accessory_ring, accessory_amulet, shield.
 *
 * For weapons + accessories the kind alone isn't enough — we use a
 * keyword heuristic on item-ID + name to pick a matching shape.
 */
function resolveIconStem(itemId: string, kind: ItemKind, name?: string): string | null {
  const tag = `${itemId} ${name ?? ''}`.toLowerCase();
  switch (kind) {
    case 'weapon':
      // Order matters — most-specific first. Lightning (zeus_bolt etc.)
      // catches before bow because the keywords could collide.
      if (/(lightning|zeus|bolt|blitz|thunder|spark)/.test(tag))              return 'weapon_lightning';
      if (/(staff|stab|wand|scepter|sceptre|rod|focus|sigil)/.test(tag))      return 'weapon_staff';
      if (/(axe|cleaver)/.test(tag))                                          return 'weapon_axe';
      if (/(hammer|warhammer|crusher|maul|smasher)/.test(tag))                return 'weapon_hammer';
      if (/(bow|cannon|gun|rifle|launcher|crossbow)/.test(tag))               return 'weapon_bow';
      return 'weapon_sword';
    case 'helmet':    return 'helmet';
    case 'armor':     return 'armor';
    case 'shield':    return 'shield';
    case 'accessory':
      if (/(ring|band|loop)/.test(tag))                                       return 'accessory_ring';
      return 'accessory_amulet';   // amulet/pendant/sigil/core/charm/etc.
    default:          return null;
  }
}

/** Lucide fallback for resource items (no Blender render available). */
const KIND_ICON: Partial<Record<ItemKind, LucideIcon>> = {
  hardware:    Cog,
  energy_cell: Battery,
  data_track:  Cpu,
  chip:        Cpu,
  junk:        Trash2,
};

const RARITY_COLOR: Record<Rarity, string> = {
  common:    '#a4b8c8',
  uncommon:  '#4affb0',
  rare:      '#4aa7ff',
  epic:      '#c64aff',
  legendary: '#ff9a2b',
  mythic:    '#ff4af0',
};

export function ItemIcon({ itemId, sizeRem = 3, className = '' }: ItemIconProps) {
  // Hand-drawn pixel-art weapons get their own CSS-grid sprite renderer.
  if (itemId === 'zeus_bolt') {
    return <ZeusBoltSprite sizeRem={sizeRem} className={`item-icon ${className}`} />;
  }

  // Hand-drawn legacy sprite-sheet entries (only ~13 items)
  const frame = ITEM_SPRITES[itemId];
  if (frame) {
    return (
      <div
        className={`item-icon ${className}`}
        style={getSpriteStyle(frame, sizeRem)}
        aria-hidden="true"
      />
    );
  }

  const def = getItem(itemId);

  // Equipment: use Blender-rendered PNG by kind+variant+rarity.
  if (def) {
    const stem = resolveIconStem(itemId, def.kind, def.nameKey);
    if (stem) {
      return (
        <div
          className={`item-icon ${className}`}
          style={{
            width: `${sizeRem}rem`,
            height: `${sizeRem}rem`,
            backgroundImage: `url('/assets/items/${stem}_${def.rarity}.png')`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
          aria-hidden="true"
        />
      );
    }
  }

  // Resource fallback: Lucide icon by item kind, tinted by rarity.
  const Icon = def ? (KIND_ICON[def.kind] ?? Package) : Package;
  const tint = def ? RARITY_COLOR[def.rarity] : '#888';
  return (
    <div
      className={`item-icon item-icon--glyph ${className}`}
      style={{
        width: `${sizeRem}rem`,
        height: `${sizeRem}rem`,
        display: 'grid',
        placeItems: 'center',
        background: `radial-gradient(circle at 50% 45%, ${tint}33 0%, ${tint}0d 60%, transparent 100%)`,
      }}
      aria-hidden="true"
    >
      <Icon
        size={Math.round(sizeRem * 16 * 0.55)}
        color={tint}
        strokeWidth={1.75}
        style={{ filter: `drop-shadow(0 0 4px ${tint}66)` }}
      />
    </div>
  );
}
